import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useRouter } from 'next/router';
import GoogleMapReact from 'google-map-react';
// @ts-ignore
import MarkerClusterer from '@google/markerclusterer';
import clsx from 'clsx';

import ListingImagesCarousel from '@components/_shared/listingImagesCarousel';
import {
  getBoundsStringFromMap,
  getInfoWindowPosition,
  renderMobileListingPopup,
} from '@components/_shared/googleMaps/mapUtils';
import Button from '@components/_shared/button';
import { BaseComponentProps } from '@utils/types/baseComponents';
import { LatLng } from '@utils/types/locations';
import { MapBounds, MarkerType } from '@utils/types/maps';
import envVars from '@utils/config/env';
import { useIsMobile } from '@utils/hooks/useIsMobile';
import { useGetListingLink } from '@utils/hooks/useGetListingLink';

import styles from './styles.module.scss';

type Props = {
  zoom: number;
  center: LatLng;
  markers: MarkerType[];
  bounds?: MapBounds;
  onMarkerClick?: (id: string) => void;
  callbackOnBoundsChange?: (bounds: string) => void;
  onInfoWindowClose?: () => void;
  market?: string | null;
  resetOnBoundsChange: boolean;
  buildMarker: (props: MarkerType) => any;
  type: 'default';
} & BaseComponentProps;

GoogleMaps.defaultProps = {
  center: { lat: 40.73061, lng: -73.935242 }, // New York
  resetOnBoundsChange: false,
  type: 'default',
};

export function GoogleMaps(props: PropsWithChildren<Props>) {
  const {
    center,
    zoom,
    containerStyle,
    bounds,
    markers,
    onMarkerClick,
    callbackOnBoundsChange,
    onInfoWindowClose,
    market,
    resetOnBoundsChange,
    buildMarker,
    type,
  } = props;
  const [mapLoaded, setMapLoaded] = useState(false);
  const [initialBounds, setInitialBounds] = useState<MapBounds>();
  const [isBoundsChangedByUser, setIsBoundsChangedByUser] = useState(false);
  const isDefToBoundsChange = useRef(false);
  const mapRef = useRef<google.maps.Map>();
  const googleRef = useRef<any>();
  const clustererRef = useRef<any>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow>();
  const isMobile = useIsMobile();
  const router = useRouter();
  const { prepareListingHref } = useGetListingLink();

  const activeListing = useMemo(() => {
    const activeMarker = markers.find((el) => el.isActive);
    if (!activeMarker) return null;
    return {
      listingInfo: activeMarker.listingInfo
        ? { ...activeMarker.listingInfo, price: activeMarker.price }
        : null,
    };
  }, [markers]);

  const setGoogleMapRef = ({ map, maps }: any) => {
    mapRef.current = map;
    googleRef.current = maps;
    infoWindowRef.current = new google.maps.InfoWindow({
      content: '',
      disableAutoPan: true,
    });
    clustererRef.current = new MarkerClusterer(map, [], {
      imagePath: '/icons/cluster.svg',
      gridSize: 30,
      styles: [
        {
          textColor: 'white',
          textSize: 16,
          url: '/icons/cluster.svg',
          height: 48,
          width: 48,
        },
      ],
    });
    setMapLoaded(true);
  };

  const onBoundsChange = useCallback(() => {
    if (callbackOnBoundsChange) {
      if (isDefToBoundsChange.current) {
        setTimeout(() => (isDefToBoundsChange.current = false), 100);
      } else {
        setIsBoundsChangedByUser(true);
        callbackOnBoundsChange(
          !!mapRef.current ? getBoundsStringFromMap(mapRef.current) : ''
        );
      }
    }
  }, [callbackOnBoundsChange, mapRef.current]);

  /**
   * Map events listeners here.
   * Register event only if the initial calculations are
   * already done (initialBounds set in our case)
   */
  useEffect(() => {
    if (mapLoaded && mapRef.current && !!initialBounds) {
      if (!!onInfoWindowClose && infoWindowRef.current) {
        infoWindowRef.current.addListener('closeclick', onInfoWindowClose);
      }
      if (!!callbackOnBoundsChange) {
        mapRef.current.addListener('bounds_changed', onBoundsChange);
      }
    }
  }, [mapLoaded, initialBounds]);

  useEffect(() => {
    if (mapLoaded) {
      if (clustererRef.current) clustererRef.current.clearMarkers();
      if (!!markers.length && googleRef.current && clustererRef.current) {
        const markersForClusterer = markers.map((props) => {
          const marker: google.maps.Marker = new googleRef.current.Marker(
            buildMarker(props)
          );
          if (onMarkerClick && props.id) {
            marker.addListener('click', () => {
              onMarkerClick(props.id!);
            });
          }
          if (!isMobile && props.isActive) {
            if (infoWindowRef.current) {
              infoWindowRef.current.setContent(props.infoWindowContent);
              infoWindowRef.current.setOptions({
                pixelOffset: getInfoWindowPosition(
                  props.position,
                  mapRef.current
                ),
              });
              infoWindowRef.current.open({
                anchor: marker,
                map: mapRef.current,
                shouldFocus: false,
              });
              google.maps.event.addListener(
                infoWindowRef.current,
                'domready',
                function () {
                  const listingCarouselElement =
                    document.getElementById('listing-carousel');
                  if (listingCarouselElement) {
                    ReactDOM.render(
                      <ListingImagesCarousel
                        photos={activeListing?.listingInfo?.photos || []}
                        containerStyle={styles.imageContainer}
                        onClick={() =>
                          router.push(
                            prepareListingHref({
                              listingSlug: activeListing?.listingInfo?.url_slug,
                              preserveQuery: true,
                            })
                          )
                        }
                      />,
                      listingCarouselElement
                    );
                  }
                }
              );
            }
          }
          return marker;
        });
        clustererRef.current.addMarkers(markersForClusterer);
      }
    }
  }, [markers, mapLoaded]);

  const fitBounds = (boundToSet = bounds) => {
    if (!!boundToSet && !!mapRef.current) {
      mapRef.current?.fitBounds(boundToSet);
      if (mapRef.current?.getZoom()! > 15) {
        mapRef.current?.setZoom(15);
      }
    }
  };

  useEffect(() => {
    if (!callbackOnBoundsChange && center && mapRef.current) {
      mapRef.current.setCenter(center);
    }
  }, [center]);

  /**
   * The bounds will be set only for the initial
   * rendering and not on the changes
   */
  useEffect(() => {
    if (!!bounds && !initialBounds && mapLoaded) {
      setTimeout(() => fitBounds(), 0);
      setTimeout(() => setInitialBounds(bounds), 500);
    }
  }, [bounds, mapLoaded]);

  /**
   * The bounds will be change on the change of bounds value
   * if resetOnBoundsChange is true
   */
  useEffect(() => {
    if (!!bounds && resetOnBoundsChange) {
      setTimeout(() => fitBounds(), 0);
      setTimeout(() => {
        setIsBoundsChangedByUser(false);
        setInitialBounds(bounds);
      }, 500);
    }
  }, [bounds, mapLoaded]);

  const onResetBoundsClick = () => {
    fitBounds(initialBounds);
    setTimeout(() => setIsBoundsChangedByUser(false), 50);
  };

  /**
   * If market is changed by user the bounds should be recalculated
   */
  useEffect(() => {
    if (initialBounds) {
      if (!!market || (!market && typeof market === 'string')) {
        setIsBoundsChangedByUser(true);
        isDefToBoundsChange.current = true;
        fitBounds();
      }
    }
  }, [markers]);

  return (
    <div className={clsx([styles.container, containerStyle])}>
      {!!callbackOnBoundsChange && isBoundsChangedByUser && (
        <Button
          onClick={onResetBoundsClick}
          containerStyle={styles.buttonOverMap}
          variant="blueContained"
        >
          Return to Original Position
        </Button>
      )}

      <div className={styles.map}>
        <GoogleMapReact
          bootstrapURLKeys={{ key: envVars.googleMapsApiKey }}
          defaultCenter={center}
          defaultZoom={zoom}
          onGoogleApiLoaded={setGoogleMapRef}
          yesIWantToUseGoogleMapApiInternals
          shouldUnregisterMapOnUnmount
          options={{
            fullscreenControl: false,
          }}
        />
      </div>

      {isMobile &&
        type === 'default' &&
        !!activeListing?.listingInfo &&
        renderMobileListingPopup(
          activeListing.listingInfo as any,
          () => onInfoWindowClose?.(),
          prepareListingHref({
            listingSlug: activeListing.listingInfo.url_slug,
            preserveQuery: true,
          })
        )}
    </div>
  );
}
